import { useMemo } from 'react'
import { useResultsStore, useStiffeningStore, useTransmissionStore } from '@editorStores'
import { useGuidToElement } from '@editorHooks'
import { AnchorSide, getAnchorSides } from '../utils'

/**
 * Which side of its stiffening segment each tensile anchor sits on, keyed by support guid.
 *
 * Shared by everything that has to name an anchor ("links"/"rechts"/"unten"): the anchor list
 * rows, the anchor detail pane, and the relation links that jump between the Wandscheibe, Anker
 * and Schubanker panes. The normalization is subtle enough — stiffening intervals are absolute
 * metres along the wall while support positions are relative — that it should exist once.
 */
const useAnchorSides = (): Record<string, AnchorSide> => {
  const guidToElement = useGuidToElement()
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const tensileTransmissionGraph = useTransmissionStore(state => state.tensileTransmissionGraph)

  return useMemo(() => {
    const supportRelativePositions: Record<string, number | undefined> = {}
    for (const support of tensileTransmissionGraph?.element_supports ?? []) {
      supportRelativePositions[support.guid] =
        support.relative_position != null ? parseFloat(support.relative_position) : undefined
    }
    // Stiffening intervals are absolute meters along the wall; the support positions are
    // relative (0..1), so normalize the midpoint by the wall length before comparing.
    const segmentMidpoints: Record<string, number | undefined> = {}
    for (const segment of stiffeningIntervals ?? []) {
      if (!segment.guid) continue
      const element = guidToElement[segment.element_guid] as ShapeObject | undefined
      const points = element?.shape?.points
      const length = points && points.length >= 2 ? points[0].distanceTo(points[1]) : undefined
      segmentMidpoints[segment.guid] = length
        ? (parseFloat(segment.interval.lower) + parseFloat(segment.interval.upper)) / 2 / length
        : undefined
    }
    return getAnchorSides(
      anchorInterventionTableData ?? [],
      supportRelativePositions,
      segmentMidpoints,
    )
  }, [anchorInterventionTableData, guidToElement, stiffeningIntervals, tensileTransmissionGraph])
}

export default useAnchorSides
